// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { bool, array, func } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { uniqueId } from 'lodash';
import {
  SkeletonPlaceholder,
  SkeletonText,
} from '@carbon/react';

import { AnalyticsLink } from 'Components/shared';

import XlsIcon from 'Assets/images/xls.svg';
import CsvIcon from 'Assets/images/csv.svg';
import PdfIcon from 'Assets/images/pdf.svg';
import HtmlIcon from 'Assets/images/html.svg';

const StyledAccountInfo = styled.div`
  section {
    margin-bottom: 16px;
    border: 1px solid #e2e2e2;

    h4 {
      font-size: 16px;
      padding: 16px;
      background: #f5f7f9;
    }

    .file {
      display: flex;
      align-items: center;
      padding: 16px;
      border-bottom: 1px solid #e2e2e2;

      .fileIcon {
        margin-right: 16px;

        svg {
          width: 40px;
          height: 50px;
        }
      }

      .fileName {
        &.skeleton {
          flex: 1;
        }

        .fileTitle {
          button {
            background: transparent;
            border: none;
            padding: 0;
            font-size: 16px;
            color: #3a3a3a;
            cursor: pointer;
          }

          a {
            word-break: break-all;
            color: #3a3a3a;
          }
        }

        .fileDate {
          margin-top: 4px;

          span {
            color: #707070;
          }
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .pageTitle {
      margin-bottom: 20px;
    }

    .file .fileName .fileTitle button {
      text-align: left;
      word-break: break-all;
    }
  }
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 3rem;
  width: 3rem;
`;

const AccountInfo = ({
  loading,
  informationData,
  handleDownload,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const renderFileIcon = type => {
    switch (type) {
      case 'xls':
        return <XlsIcon />;
      case 'csv':
        return <CsvIcon />;
      case 'pdf':
        return <PdfIcon />;
      default:
        return <HtmlIcon />;
    }
  };

  const [procedures, pricesAndOffers] = (informationData || []).reduce(
    (acc, item) => {
      if (item?.informationType === 2) {
        acc[0].push(item);
      } else {
        acc[1].push(item);
      }
      return acc;
    },
    [[], []]
  );

  const renderDownloadsList = data => (
    <div className="sectionBody">
      { data.map(item => (
        <div className="file" key={ item?.uploadedInformationId }>
          <div className="fileIcon">
            { renderFileIcon(item?.type) }
          </div>
          <div className="fileName">
            <div className="fileTitle">
              { item?.fileName ? (
                <button
                  type="button"
                  onClick={ () => {
                    if (item?.uploadedInformationId === 0) {
                      handleDownload(item.linkUrl);
                    } else {
                      handleDownload(`/account/info/${ item?.uploadedInformationId }`);
                    }
                  } }
                >
                  { item?.fileName || item?.linkName }
                </button>
              ) : (
                <AnalyticsLink
                  to={ item?.linkUrl }
                  target="_blank"
                  event={ { action: 'linked_clicked' } }
                >
                  { item?.linkName }
                </AnalyticsLink>
              ) }
            </div>
            <div className="fileDate"><span>{ t('profile.updatedAt') }: </span>{ item?.updatedAt }</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <StyledAccountInfo theme={ theme }>
      { loading ? Array.from({ length: 2 }, () => (
        <section key={ uniqueId('section') }>
          <h4><SkeletonText /></h4>
          { Array.from({ length: 3 }, () => (
            <div className="file" key={ uniqueId('procedures') }>
              <div className="fileIcon">
                <StyledSkeletonPlaceholder />
              </div>
              <div className="fileName skeleton">
                <SkeletonText paragraph lineCount={ 2 } />
              </div>
            </div>
          ))}
        </section>
      )) : (
        <>
          <section>
            <h4>{ t('profile.procedures') }</h4>
            { renderDownloadsList(procedures) }
          </section>
          <section>
            <h4>{ t('profile.pricesAndOffers') }</h4>
            { renderDownloadsList(pricesAndOffers) }
          </section>
        </>
      )}
    </StyledAccountInfo>
  );
};

AccountInfo.propTypes = {
  loading: bool,
  informationData: array,
  handleDownload: func,
};

AccountInfo.displayName = 'AccountInfoComponent';

export default AccountInfo;
